import {
  authFormClass,
  authPageContainerClass,
} from "@/layout/auth-layout.styles";
import { Button, Typography } from "@iluvatar/ui-kit";
import { useState } from "react";

import type { FormHelpers } from "@/FormLib";
import { Form } from "@/components/Form";
import DatePicker from "@/components/Form/Fields/Date";
import { MemoTextField } from "@/components/Form/Fields/Input";
import { formErrorHandler } from "@/components/Form/helpers";
import { useAuthenticationContext } from "@/contexts/authentication/authentication.context";
import { usePageLoading } from "@/contexts/page-loading.context";
import requestService from "@/services/request.service";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as Yup from "yup";

type SetupForm = { displayName: string; birthDate: number | undefined };
const setupValidation = Yup.object({
  displayName: Yup.string().trim().required("Display name is required"),
  birthDate: Yup.number()
    .required("Date of birth is required")
    .test("adult", "You must be 18 years or older", (value) => {
      const cutoff = new Date();
      cutoff.setFullYear(cutoff.getFullYear() - 18);
      return value !== undefined && value <= cutoff.getTime();
    }),
});

function AccountSetupPage() {
  const { user, accountDetails, reload, logout } = useAuthenticationContext();
  const navigate = useNavigate();
  const [disabled, setDisabled] = useState(false);
  usePageLoading(disabled);

  const submit = async (
    details: SetupForm,
    helpers: FormHelpers<SetupForm>,
  ) => {
    setDisabled(true);
    try {
      await requestService.request("/api/account/setup", {
        method: "post",
        body: JSON.stringify(details),
      });
      await reload();
      await navigate({
        to: "/user/$userId/edit",
        params: { userId: user!.uid },
      });
    } catch (error) {
      formErrorHandler(error, helpers);
    } finally {
      setDisabled(false);
    }
  };

  return (
    <div className={`${authPageContainerClass} mx-auto`}>
      <Typography
        variant="h5"
        className={[
          "auth-title",
          "[color:rgb(var(--uik-subtle))] [margin-top:24px]",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        Complete your account
      </Typography>
      <Typography
        className={[
          "max-w-[420px] px-6 text-center",
          "[color:rgb(var(--uik-subtle))]",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        Your account is created. Add your display name and date of birth to
        continue.
      </Typography>
      <Form<SetupForm>
        id="account-setup"
        outerStyle={{ alignItems: "center" }}
        disabled={disabled}
        validationSchema={setupValidation}
        initialValues={{
          displayName: accountDetails?.displayName ?? user?.displayName ?? "",
          birthDate: accountDetails?.birthDate ?? undefined,
        }}
        onSubmit={submit}
        className={`${authFormClass} max-w-[420px]`}
      >
        <MemoTextField
          required
          label="Display Name"
          name="displayName"
          className="auth-form-element"
        />
        <DatePicker
          required
          outputNumber
          label="Date of Birth"
          name="birthDate"
          className="auth-form-element"
        />
        <Button
          variant="outline"
          type="submit"
          disabled={disabled}
          className={["auth-form-element auth-button", "w-full"]
            .filter(Boolean)
            .join(" ")}
        >
          Continue
        </Button>
      </Form>
      <Button
        variant="outline"
        disabled={disabled}
        onClick={async () => {
          await logout();
          await navigate({ to: "/login" });
        }}
      >
        Sign out
      </Button>
    </div>
  );
}

export const Route = createFileRoute("/account-setup")({
  component: AccountSetupPage,
});
